import React, { useState, useEffect, useRef } from 'react';
import { ArrowRight, ShieldCheck, CheckCircle2, HeartPulse, Bell, Calendar, Plus, Lock } from 'lucide-react';

interface HeroEnhancedProps {
  onAddMedication?: () => void;
  isFirstRun?: boolean;
}

const HeroEnhanced: React.FC<HeroEnhancedProps> = ({ onAddMedication, isFirstRun = true }) => {
  const [mounted, setMounted] = useState(false);
  const prefersReducedMotion = useRef(false);

  useEffect(() => {
    prefersReducedMotion.current = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    setMounted(true);
  }, []);

  const handleStartFree = () => {
    if (onAddMedication) {
      onAddMedication();
    } else {
      window.location.href = '/signup';
    }
  };

  const handleExploreFeatures = () => {
    const featuresEl = document.getElementById('features');
    if (featuresEl) {
      featuresEl.scrollIntoView({ behavior: prefersReducedMotion.current ? 'auto' : 'smooth' });
    } else {
      window.location.href = '/features';
    }
  };

  const isVisible = mounted || prefersReducedMotion.current;

  return (
    <section
      className="relative min-h-[100dvh] w-full flex items-center justify-center overflow-hidden bg-white"
      aria-label="Meduler hero — effortless medication management"
    >
      {/* Background gradient */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{ background: 'linear-gradient(to bottom, #f8fafc, #eff6ff)' }}
        aria-hidden="true"
      />

      {/* Subtle grid pattern */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          opacity: 0.025,
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0 0h40v40H0V0zm39 39V1H1v38h38z' fill='%230f172a' fill-opacity='1' fill-rule='evenodd'/%3E%3C/svg%3E")`,
        }}
        aria-hidden="true"
      />

      {/* Decorative plus crosses */}
      <div className="absolute top-20 left-10 text-blue-400/20 pointer-events-none" aria-hidden="true">
        <Plus size={24} />
      </div>
      <div className="absolute top-36 right-16 text-blue-400/10 pointer-events-none" aria-hidden="true">
        <Plus size={40} />
      </div>
      <div className="absolute bottom-24 left-1/4 text-blue-400/15 pointer-events-none" aria-hidden="true">
        <Plus size={32} />
      </div>
      <div className="absolute top-1/3 right-1/3 text-blue-400/10 pointer-events-none" aria-hidden="true">
        <Plus size={16} />
      </div>

      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 py-20 lg:py-28">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-12 items-center">

          {/* ─── Left: copy ─── */}
          <div className="flex flex-col items-start text-left max-w-2xl mx-auto lg:mx-0">

            {/* Badge */}
            <div
              className="inline-flex items-center px-3 py-1.5 rounded-full bg-white/80 backdrop-blur-md border border-blue-100 shadow-sm mb-8"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: prefersReducedMotion.current ? 'none' : 'opacity 600ms cubic-bezier(0.16,1,0.3,1), transform 600ms cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <ShieldCheck className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" aria-hidden="true" />
              <span className="text-sm font-medium text-slate-700">Intelligent Medication Management</span>
            </div>

            {/* Headline */}
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-bold text-[#0f172a] leading-[1.08] tracking-tight mb-6"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: prefersReducedMotion.current ? 'none' : 'opacity 600ms 100ms cubic-bezier(0.16,1,0.3,1), transform 600ms 100ms cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              Effortless Medication Management
            </h1>

            {/* Sub-headline */}
            <p
              className="text-lg sm:text-xl text-slate-500 leading-relaxed mb-10 max-w-xl"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: prefersReducedMotion.current ? 'none' : 'opacity 600ms 200ms cubic-bezier(0.16,1,0.3,1), transform 600ms 200ms cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              Helping Patients &amp; Caregivers With An Intelligent Virtual Pillbox
            </p>

            {/* CTAs */}
            <div
              className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto mb-10"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(16px)',
                transition: prefersReducedMotion.current ? 'none' : 'opacity 600ms 300ms cubic-bezier(0.16,1,0.3,1), transform 600ms 300ms cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              <button
                onClick={handleStartFree}
                className="inline-flex items-center justify-center px-8 py-3.5 bg-[#2563eb] hover:bg-blue-700 active:bg-blue-800 text-white rounded-full font-medium transition-colors shadow-sm hover:shadow-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 group"
                aria-label={isFirstRun ? 'Start for free and add your medications' : 'Sign up for free'}
              >
                Start for Free
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </button>

              <button
                onClick={handleExploreFeatures}
                className="inline-flex items-center justify-center px-8 py-3.5 bg-white border border-slate-200 hover:border-blue-200 hover:bg-blue-50 text-slate-700 rounded-full font-medium transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-400"
              >
                Explore Features
              </button>
            </div>

            {/* Trust signals */}
            <div
              className="flex flex-wrap items-center gap-5 sm:gap-8"
              style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? 'translateY(0)' : 'translateY(12px)',
                transition: prefersReducedMotion.current ? 'none' : 'opacity 600ms 400ms cubic-bezier(0.16,1,0.3,1), transform 600ms 400ms cubic-bezier(0.16,1,0.3,1)',
              }}
            >
              {[
                { Icon: CheckCircle2, text: 'Free to start' },
                { Icon: Lock, text: 'Private & Secure' },
                { Icon: HeartPulse, text: 'Caregiver Support' },
              ].map(({ Icon, text }) => (
                <div key={text} className="flex items-center text-sm text-slate-600 font-medium">
                  <Icon className="w-4 h-4 text-blue-500 mr-2 flex-shrink-0" aria-hidden="true" />
                  {text}
                </div>
              ))}
            </div>
          </div>

          {/* ─── Right: phone mockup ─── */}
          <div
            className="relative flex justify-center lg:justify-end"
            style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? 'translateY(0)' : 'translateY(24px)',
              transition: prefersReducedMotion.current ? 'none' : 'opacity 800ms 250ms cubic-bezier(0.16,1,0.3,1), transform 800ms 250ms cubic-bezier(0.16,1,0.3,1)',
            }}
            aria-hidden="true"
          >
            {/* Soft glow behind phone */}
            <div className="absolute inset-0 bg-blue-200/40 rounded-full blur-[80px] transform scale-75 pointer-events-none" />

            <div
              className="relative w-[300px] sm:w-[320px]"
              style={{ animation: prefersReducedMotion.current ? 'none' : 'heroFloat 4s ease-in-out infinite' }}
            >
              {/* Phone shell */}
              <div className="bg-white rounded-[40px] shadow-2xl border-[7px] border-slate-100 overflow-hidden ring-1 ring-slate-900/5">

                {/* App header */}
                <div className="bg-gradient-to-br from-[#3b82f6] to-[#4f46e5] px-5 pt-10 pb-5 text-white rounded-b-3xl">
                  <div className="flex justify-between items-center mb-6">
                    <span className="font-semibold text-lg tracking-wide">Meduler</span>
                    <div className="relative">
                      <Bell className="w-5 h-5 opacity-90" />
                      <span className="absolute -top-0.5 -right-0.5 w-2 h-2 bg-red-400 rounded-full border border-[#4f46e5]" />
                    </div>
                  </div>
                  <div>
                    <p className="text-blue-100 text-xs font-medium uppercase tracking-wider mb-1">Your next dose</p>
                    <h3 className="text-xl font-bold mb-1.5">Lisinopril 10mg</h3>
                    <div className="flex items-center text-blue-100 text-sm">
                      <Calendar className="w-3.5 h-3.5 mr-1.5 opacity-80" />
                      Today, 8:00 PM
                    </div>
                  </div>
                </div>

                {/* App body */}
                <div className="px-4 py-5 bg-slate-50/60 min-h-[280px]">
                  <div className="space-y-2.5 mb-4">
                    {/* Taken */}
                    <div className="bg-white px-4 py-3 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-slate-800 font-semibold text-sm">Metformin 500mg</p>
                        <p className="text-slate-400 text-xs">8:00 AM</p>
                      </div>
                      <span className="px-2.5 py-0.5 bg-green-50 text-green-600 text-[10px] font-bold uppercase tracking-wide rounded-full">Taken</span>
                    </div>

                    {/* Upcoming (highlighted) */}
                    <div className="bg-white px-4 py-3 rounded-2xl shadow-sm border-l-4 border-l-blue-500 flex items-center justify-between relative overflow-hidden">
                      <div className="absolute inset-0 bg-blue-50/30" />
                      <div className="relative z-10">
                        <p className="text-slate-800 font-semibold text-sm">Lisinopril 10mg</p>
                        <p className="text-blue-500 font-medium text-xs">8:00 PM</p>
                      </div>
                      <span className="relative z-10 px-2.5 py-0.5 bg-blue-50 text-blue-600 text-[10px] font-bold uppercase tracking-wide rounded-full">Upcoming</span>
                    </div>

                    {/* Upcoming */}
                    <div className="bg-white px-4 py-3 rounded-2xl shadow-sm border border-slate-100 flex items-center justify-between">
                      <div>
                        <p className="text-slate-800 font-semibold text-sm">Aspirin 81mg</p>
                        <p className="text-slate-400 text-xs">9:00 PM</p>
                      </div>
                      <span className="px-2.5 py-0.5 bg-slate-100 text-slate-500 text-[10px] font-bold uppercase tracking-wide rounded-full">Upcoming</span>
                    </div>
                  </div>

                  {/* Adherence bar */}
                  <div className="bg-white px-4 py-4 rounded-2xl shadow-sm border border-slate-100">
                    <div className="flex justify-between items-end mb-2.5">
                      <span className="text-slate-600 text-xs font-semibold">Today's Adherence</span>
                      <span className="text-blue-500 text-sm font-bold">67%</span>
                    </div>
                    <div className="h-2 w-full bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-blue-500 rounded-full"
                        style={{
                          width: isVisible ? '67%' : '0%',
                          transition: prefersReducedMotion.current ? 'none' : 'width 1000ms 600ms cubic-bezier(0.16,1,0.3,1)',
                        }}
                      />
                    </div>
                    <p className="text-slate-400 text-xs mt-1.5">2 of 3 doses taken</p>
                  </div>
                </div>
              </div>

              {/* Floating accent dots */}
              <div
                className="absolute -top-2 -right-3 w-5 h-5 bg-blue-300/40 rounded-full"
                style={{ animation: prefersReducedMotion.current ? 'none' : 'heroFloat 3s ease-in-out infinite 0.5s' }}
              />
              <div
                className="absolute top-1/4 -left-5 w-3.5 h-3.5 bg-blue-400/30 rounded-full"
                style={{ animation: prefersReducedMotion.current ? 'none' : 'heroFloat 3.5s ease-in-out infinite 1s' }}
              />
              <div
                className="absolute bottom-1/4 -right-4 w-2.5 h-2.5 bg-blue-200/40 rounded-full"
                style={{ animation: prefersReducedMotion.current ? 'none' : 'heroFloat 4s ease-in-out infinite 1.5s' }}
              />
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes heroFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </section>
  );
};

export default HeroEnhanced;
